import { useId } from "react";
import { Reveal } from "@/components/Reveal";
import { formatPrice } from "@/lib/api";

const PALETTES = [
  { bg1: "#F7F2E8", bg2: "#EDE3CF", accent: "#C9A24B", deep: "#2A241F", soft: "#FDFBF6" },
  { bg1: "#2A211A", bg2: "#171310", accent: "#D4AF37", deep: "#EFE8DC", soft: "#3A3229" },
  { bg1: "#F4EFE6", bg2: "#E7DBC2", accent: "#B89759", deep: "#3D352E", soft: "#FAF6ED" },
  { bg1: "#FBF8F1", bg2: "#F0E7D6", accent: "#D4AF37", deep: "#463C31", soft: "#FFFFFF" },
  { bg1: "#EFE8DA", bg2: "#DFD2B6", accent: "#C39A3B", deep: "#332B22", soft: "#F8F4EA" },
];

export function ArtworkSvg({ seed = 0, className = "" }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const p = PALETTES[seed % PALETTES.length];
  const v = seed % 5;
  const rays = [-22, 0, 22].map((a, i) => (
    <rect
      key={a}
      x="198.5"
      y="-30"
      width="3"
      height="250"
      fill={p.accent}
      opacity={0.5 - i * 0.13}
      transform={`rotate(${a} 200 30)`}
    />
  ));

  return (
    <svg
      viewBox="0 0 400 500"
      className={className}
      role="img"
      aria-label="Faith-inspired artwork"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={`bg${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.bg1} />
          <stop offset="1" stopColor={p.bg2} />
        </linearGradient>
        <linearGradient id={`au${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.accent} stopOpacity="0.95" />
          <stop offset="1" stopColor={p.accent} stopOpacity="0.35" />
        </linearGradient>
        <radialGradient id={`glow${uid}`} cx="0.5" cy="0.3" r="0.6">
          <stop offset="0" stopColor={p.accent} stopOpacity="0.5" />
          <stop offset="1" stopColor={p.accent} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="400" height="500" fill={`url(#bg${uid})`} />
      <rect width="400" height="500" fill={`url(#glow${uid})`} />

      {v === 0 && (
        <>
          {rays}
          <circle cx="200" cy="138" r="52" fill="none" stroke={p.accent} strokeWidth="8" opacity="0.85" />
          <circle cx="200" cy="138" r="30" fill={`url(#au${uid})`} />
          <path d="M118 500 L118 252 A82 82 0 0 1 282 252 L282 500 Z" fill={p.deep} opacity="0.94" />
          <path d="M152 500 L152 272 A48 48 0 0 1 248 272 L248 500 Z" fill={p.accent} opacity="0.28" />
        </>
      )}

      {v === 1 && (
        <>
          {rays}
          <rect x="192" y="42" width="16" height="300" fill={`url(#au${uid})`} />
          <rect x="118" y="150" width="164" height="12" fill={`url(#au${uid})`} />
          <circle cx="200" cy="156" r="92" fill="none" stroke={p.accent} strokeWidth="2" opacity="0.45" />
          <path d="M0 500 Q200 388 400 500 Z" fill={p.deep} opacity="0.9" />
          <circle cx="200" cy="156" r="18" fill={p.soft} opacity="0.9" />
        </>
      )}

      {v === 2 && (
        <>
          {rays}
          <circle cx="200" cy="200" r="112" fill="none" stroke={p.accent} strokeWidth="1.5" opacity="0.35" />
          <circle cx="200" cy="200" r="84" fill="none" stroke={p.accent} strokeWidth="4" opacity="0.55" />
          <circle cx="200" cy="200" r="58" fill="none" stroke={p.accent} strokeWidth="7" opacity="0.75" />
          <circle cx="200" cy="200" r="32" fill={p.deep} />
          <circle cx="200" cy="200" r="32" fill="none" stroke={p.accent} strokeWidth="1.5" opacity="0.9" />
          <path d="M0 500 Q200 428 400 500 Z" fill={p.deep} opacity="0.88" />
        </>
      )}

      {v === 3 && (
        <>
          {rays}
          <circle cx="200" cy="176" r="62" fill={`url(#au${uid})`} />
          <circle cx="200" cy="176" r="62" fill="none" stroke={p.accent} strokeWidth="2" opacity="0.6" />
          <path d="M0 328 Q120 288 220 322 T400 312 L400 500 L0 500 Z" fill={p.soft} />
          <path d="M0 388 Q140 348 260 382 T400 372 L400 500 L0 500 Z" fill={p.accent} opacity="0.45" />
          <path d="M0 448 Q160 412 300 442 T400 436 L400 500 L0 500 Z" fill={p.deep} opacity="0.92" />
        </>
      )}

      {v === 4 && (
        <>
          {rays}
          <path d="M110 472 L110 262 A90 90 0 0 1 290 262 L290 472" fill="none" stroke={p.deep} strokeWidth="2.5" />
          <circle cx="200" cy="152" r="27" fill="none" stroke={p.accent} strokeWidth="3" />
          <ellipse cx="120" cy="330" rx="16" ry="7" fill={p.accent} opacity="0.7" transform="rotate(-30 120 330)" />
          <ellipse cx="282" cy="300" rx="14" ry="6" fill={p.accent} opacity="0.55" transform="rotate(24 282 300)" />
          <ellipse cx="170" cy="412" rx="18" ry="8" fill={p.accent} opacity="0.6" transform="rotate(12 170 412)" />
          <ellipse cx="250" cy="430" rx="12" ry="5" fill={p.accent} opacity="0.8" transform="rotate(-18 250 430)" />
          <ellipse cx="200" cy="352" rx="10" ry="4.5" fill={p.accent} opacity="0.5" transform="rotate(40 200 352)" />
          <circle cx="145" cy="222" r="3" fill={p.accent} />
          <circle cx="262" cy="205" r="2.5" fill={p.accent} />
          <circle cx="308" cy="380" r="2.5" fill={p.accent} opacity="0.7" />
        </>
      )}

      <rect x="14" y="14" width="372" height="472" fill="none" stroke={p.accent} strokeOpacity="0.4" strokeWidth="1.5" />
    </svg>
  );
}

export function ArtworkCard({ art, onView, index = 0 }) {
  return (
    <Reveal delay={(index % 3) * 0.08} className="mb-8 break-inside-avoid">
      <article data-testid={`artwork-card-${art.id}`}>
        <button
          type="button"
          onClick={() => onView(art)}
          className="group block w-full text-left"
          data-testid={`artwork-view-${art.id}`}
        >
          <div
            className={`relative ${art.aspect || "aspect-[4/5]"} overflow-hidden rounded-sm border border-[#D4AF37]/30 bg-cream shadow-sm transition duration-500 hover:-translate-y-1.5 hover:shadow-xl`}
          >
            {art.images ? (
              <img
                src={art.images[0]}
                alt={`${art.title} — original artwork by Natalia`}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                loading="lazy"
              />
            ) : (
              <ArtworkSvg
                seed={art.seed}
                className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-[1.04]"
              />
            )}
            <span className="absolute left-3 top-3 rounded-full border border-[#D4AF37]/40 bg-[#FAF7F2]/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A6A52] backdrop-blur-sm">
              {art.category}
            </span>
            {art.images && (
              <span className="absolute bottom-3 right-3 rounded-full bg-[#D4AF37]/90 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#1C1815] backdrop-blur-sm">
                Original by Natalia
              </span>
            )}
          </div>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl leading-snug text-[#1C1815]">{art.title}</h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C7E70]">
                {[art.medium, art.size || art.dimensions].filter(Boolean).join(" · ")}
              </p>
            </div>
            <span className="whitespace-nowrap font-serif text-lg text-[#9A7A3F]">{formatPrice(art.price)}</span>
          </div>
        </button>
      </article>
    </Reveal>
  );
}
